import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Building2, Calendar, LogOut, User, Info, Phone, Menu, X, Shield, Store, Users } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';

const Navigation = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardPath = () => {
    if (user?.role === 'admin') return '/dashboard/admin';
    if (user?.role === 'seller') return '/dashboard/seller';
    if (user?.role === 'buyer') return '/dashboard/buyer';
    if (user?.role === 'rep') return '/dashboard/rep';
    return '/';
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo">
            <img src="/tradehub_logo.png" alt="TradeHub" className="h-10" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Language Selector - before Currency */}
            <LanguageSelector />
            
            {/* Currency Selector */}
            <CurrencySelector className="h-8" />
            
            <Link
              to="/"
              className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
              data-testid="nav-home-link"
            >
              <Building2 className="inline h-4 w-4 mr-1" />
              {t('nav.home')}
            </Link>
            <Link
              to="/events"
              className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
              data-testid="nav-events-link"
            >
              <Calendar className="inline h-4 w-4 mr-1" />
              {t('nav.events')}
            </Link>
            <Link
              to="/exhibitors"
              className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
              data-testid="nav-exhibitors-link"
            >
              <Store className="inline h-4 w-4 mr-1" />
              {t('nav.exhibitors')}
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
              data-testid="nav-about-link"
            >
              <Info className="inline h-4 w-4 mr-1" />
              {t('nav.about')}
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
              data-testid="nav-contact-link"
            >
              <Phone className="inline h-4 w-4 mr-1" />
              {t('nav.contact')}
            </Link>

            {user ? (
              <>
                {/* User Info with Role Badge */}
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                  {user.role === 'admin' && <Shield className="h-4 w-4 text-red-600" />}
                  {user.role === 'seller' && <Store className="h-4 w-4 text-accent" />}
                  {user.role === 'buyer' && <Users className="h-4 w-4 text-primary" />}
                  {user.role === 'rep' && <Users className="h-4 w-4 text-purple-600" />}
                  <span className="text-sm font-medium text-slate-700">{user.name}</span>
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${
                      user.role === 'admin' ? 'bg-red-100 text-red-700' :
                      user.role === 'seller' ? 'bg-orange-100 text-orange-700' :
                      user.role === 'rep' ? 'bg-purple-100 text-purple-700' :
                      'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {user.role === 'admin' ? 'Admin' : user.role === 'seller' ? 'Vendor' : user.role === 'rep' ? 'Rep' : 'Attendee'}
                  </Badge>
                </div>
                <Link
                  to={getDashboardPath()}
                  className="text-sm font-medium text-slate-700 hover:text-primary transition-colors"
                  data-testid="nav-dashboard-link"
                >
                  <User className="inline h-4 w-4 mr-1" />
                  {t('nav.dashboard')}
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <Link to="/auth">
                <Button size="sm" data-testid="nav-login-btn">
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col space-y-4">
              {/* Language and Currency selectors for mobile */}
              <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                <LanguageSelector />
                <CurrencySelector className="h-8" />
              </div>
              
              <Link
                to="/"
                className="text-sm font-medium text-slate-700 hover:text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building2 className="inline h-4 w-4 mr-2" />
                {t('nav.home')}
              </Link>
              <Link
                to="/events"
                className="text-sm font-medium text-slate-700 hover:text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Calendar className="inline h-4 w-4 mr-2" />
                {t('nav.events')}
              </Link>
              <Link
                to="/exhibitors"
                className="text-sm font-medium text-slate-700 hover:text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Store className="inline h-4 w-4 mr-2" />
                {t('nav.exhibitors')}
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-slate-700 hover:text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Info className="inline h-4 w-4 mr-2" />
                {t('nav.about')}
              </Link>
              <Link
                to="/contact"
                className="text-sm font-medium text-slate-700 hover:text-primary"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Phone className="inline h-4 w-4 mr-2" />
                {t('nav.contact')}
              </Link>
              {user ? (
                <>
                  <Link
                    to={getDashboardPath()}
                    className="text-sm font-medium text-slate-700 hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="inline h-4 w-4 mr-2" />
                    {t('nav.dashboard')}
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button size="sm" className="w-full">
                    {t('nav.login')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
